import React from 'react';
import { cn } from '../../lib/utils';

/**
 * EmptyState — shown when a list/table/section has no data.
 *
 * Props:
 *   icon         - Lucide icon component (rendered 48px)
 *   title        - primary heading (required)
 *   description  - secondary helper text
 *   actionLabel  - CTA button label
 *   onAction     - CTA callback
 *   secondaryLabel - ghost button label (optional)
 *   onSecondary    - ghost callback
 *   size         - 'sm' | 'md' (default 'md')
 *   className    - extra classes
 */
const SIZE = {
  sm: { wrap: 'py-8 px-4', icon: 36, title: 'text-base', desc: 'text-xs' },
  md: { wrap: 'py-14 px-6', icon: 48, title: 'text-lg', desc: 'text-sm' },
};

const EmptyState = ({
  icon: Icon = null,
  title = 'لا توجد بيانات',
  description = '',
  actionLabel = '',
  onAction = null,
  secondaryLabel = '',
  onSecondary = null,
  size = 'md',
  className = '',
}) => {
  const s = SIZE[size] || SIZE.md;

  return (
    <div
      role="status"
      aria-label={title}
      className={cn('flex flex-col items-center justify-center text-center', s.wrap, className)}
    >
      {Icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
          <Icon
            size={s.icon - 18}
            strokeWidth={1.75}
            className="text-neutral-400 dark:text-neutral-500"
            aria-hidden="true"
          />
        </div>
      )}

      <h3 className={cn('font-semibold text-neutral-800 dark:text-neutral-100', s.title)}>
        {title}
      </h3>

      {description && (
        <p className={cn('mt-1.5 text-neutral-500 dark:text-neutral-400 max-w-md leading-relaxed', s.desc)}>
          {description}
        </p>
      )}

      {(actionLabel && onAction) || (secondaryLabel && onSecondary) ? (
        <div className="mt-5 flex items-center gap-2">
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              {actionLabel}
            </button>
          )}
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              onClick={onSecondary}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 text-sm font-medium border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default EmptyState;
